/* eslint-disable react-hooks/exhaustive-deps */
import { useAppDispatch, useInquiryState, useReviewState } from '@core/configureStore';
import React, { useEffect } from 'react';
import { fetchReviews } from '@core/features/review/thunks';
import { Button, Col, Divider, Row, Space } from 'antd';
import ReviewTable from '@components/ReviewTable';
import ReviewFilterButton from '@components/ReviewFilterButton';
import ReviewSortByButton from '@components/ReviewSortbyButton';
import ReviewOrderbyButton from '@components/ReviewOrderbyButton';
import { ReloadOutlined } from '@ant-design/icons';
import FlexDatePicker from '@components/FlexDatePicker';
import moment, { Moment } from 'moment';
import { selectDateRange } from '@core/features';
import { GoogleReview } from '@core/models';
import LoadableContainer from '@components/LoadableContainer';
import SplashScreen from '@components/SplashScreen';
import AnimatedComponent from '@components/AnimatedComponent';
import NumberOfReviewsWidget from '@components/NumberOfReviewsWidget';
import AverageRatingWidget from '@components/AverageRatingWidget';
import { MIDDLE_STYLE } from '@constants/responsive';

const { RangePicker } = FlexDatePicker;

const ReviewScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { reviewResponse, dateRange, isLoading: isLoadingReview } = useReviewState();
  const { isLoading: isLoadingInquiry } = useInquiryState();
  const [rangedReviews, setRangedReviews] = React.useState<GoogleReview[]>([]);
  const handleRefresh = () => dispatch(fetchReviews());

  const handleCalendarChange = (value: Moment[]) => {
    const [startDate, endDate] = value;
    dispatch(
      selectDateRange({
        startDate,
        endDate,
      })
    );
  };

  const handleReset = () => {
    dispatch(selectDateRange(undefined));
    handleRefresh();
  };

  useEffect(() => {
    let localReviews: GoogleReview[] = reviewResponse?.reviews || [];

    if (dateRange) {
      localReviews = localReviews.filter(review =>
        moment(review.updateTime).isBetween(dateRange.startDate, dateRange.endDate)
      );
    }
    setRangedReviews(localReviews);
  }, [dateRange, reviewResponse]);

  useEffect(() => {
    handleRefresh();
  }, [dateRange]);

  return (
    <React.Fragment>
      <LoadableContainer
        isLoading={isLoadingReview || isLoadingInquiry}
        loadComponent={<SplashScreen />}>
        <div className="screen-card">
          <div style={{ ...MIDDLE_STYLE, justifyContent: 'space-between' }}>
            <h3>Reviews</h3>
            <div>
              <Button style={{ marginLeft: 20 }} type="primary" onClick={handleRefresh}>
                <ReloadOutlined /> Refresh
              </Button>
            </div>
          </div>
          <Divider />
          <Row gutter={10}>
            <Col span={8}>
              <AnimatedComponent.OpacityFadeInDiv delay={200}>
                <NumberOfReviewsWidget />
              </AnimatedComponent.OpacityFadeInDiv>
            </Col>
            <Col span={8}>
              <AnimatedComponent.OpacityFadeInDiv delay={200}>
                <AverageRatingWidget />
              </AnimatedComponent.OpacityFadeInDiv>
            </Col>
          </Row>
          <Divider />
          <div style={{ display: 'flex', marginBottom: 20, justifyContent: 'space-between' }}>
            <ReviewFilterButton />
            <ReviewSortByButton />
            <ReviewOrderbyButton />
            <Space style={{ display: 'flex' }}>
              <span style={{ fontWeight: 'normal', fontSize: 15 }}>Date Range:</span>
              <RangePicker
                allowClear
                {...(!dateRange && { defaultValue: undefined })}
                onCalendarChange={handleCalendarChange as any}
              />
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </div>
          <ReviewTable reviews={rangedReviews} size="large" style={{ padding: '20px 0px' }} />
        </div>
      </LoadableContainer>
    </React.Fragment>
  );
};

export default ReviewScreen;
